import React, { Fragment, ReactNode, useMemo, useState } from 'react'
import TemplatizedTextField, { ITemplatizedTextField } from '../../../templatized-text-field/templatized-text-field'
import { Dropdown, Popover } from 'antd'
import { BulbOutlined, Loading3QuartersOutlined } from '@ant-design/icons'
import classnames from 'classnames'
import { PushlyAi } from '@pushly/aqe/lib/components'
import { urlContainsTokens } from '../../../../utils/url-contains-tokens'
import { MenuItemGroupType, MenuItemType } from 'antd/lib/menu/hooks/useItems'
import { MenuDividerType } from 'rc-menu/lib/interface'
import { Result } from '../../../../models/result'
import { getWrappedMacro, Macro } from '@pushly/models/lib/enums'
import { DomainDto } from '../../../../dtos/domain'

type TemplateLandingUrlFieldProps = ITemplatizedTextField & {
    domain: DomainDto
    hidePageAnalysisToggle?: boolean
    hidePushlyAiToggle?: boolean
    onPushlyAiClick?(url: string, mode: 'auto' | 'advanced'): void | Promise<void>
    onPageAnalysisClick?(url: string, mode: 'auto' | 'advanced'): void | Promise<void>
}

enum ActionId {
    PAGE_ANALYSIS,
    PUSHLY_AI,
}

const ENABLED_SERVICES_TAG = '{{ENABLED_SERVICES}}'

const IneligibleURLReason = {
    EMPTY: 'Landing URL cannot be empty',
    CONTAINS_MACROS: 'Landing URL cannot contain, non-domain, macros in the domain or path',
    PROTOCOL_NOT_ELIGIBLE: `Landing URL protocol is not eligible for ${ENABLED_SERVICES_TAG}`,
    DOMAIN_NOT_ELIGIBLE: `Landing URL domain is not eligible for ${ENABLED_SERVICES_TAG}`,
    NOT_VALID_URL: 'Landing URL is not a valid URL',
} as const
type IneligibleURLReason = (typeof IneligibleURLReason)[keyof typeof IneligibleURLReason]

const WEB_PROTOCOL_RGX = /https?:/
const DOMAIN_WITH_TLD_RGX = /.\../

function getEligibleURL(value: string | null | undefined, domain: DomainDto): Result<string, IneligibleURLReason> {
    try {
        if (!value) return Result.Err(IneligibleURLReason.EMPTY)

        const url = new URL(value)
        const protocol = url.protocol.toLowerCase()

        let hostname = url.hostname.toLowerCase()
        if (
            hostname.includes(getWrappedMacro(Macro.DOMAIN_URL)) ||
            hostname.includes(getWrappedMacro(Macro.DOMAIN_ID))
        ) {
            hostname = hostname
                .replace(getWrappedMacro(Macro.DOMAIN_URL), domain.name)
                .replace(getWrappedMacro(Macro.DOMAIN_ID), domain.id.toString())
        }

        if (value.includes(getWrappedMacro(Macro.DOMAIN_URL)) || value.includes(getWrappedMacro(Macro.DOMAIN_ID))) {
            value = value
                .replace(getWrappedMacro(Macro.DOMAIN_URL), domain.name)
                .replace(getWrappedMacro(Macro.DOMAIN_ID), domain.id.toString())
        }

        if (value.includes(getWrappedMacro(Macro.DOMAIN_URL)) || value.includes(getWrappedMacro(Macro.DOMAIN_ID))) {
            value = value
                .replace(getWrappedMacro(Macro.DOMAIN_URL), domain.name)
                .replace(getWrappedMacro(Macro.DOMAIN_ID), domain.id.toString())
        }

        if (urlContainsTokens(value)) {
            return Result.Err(IneligibleURLReason.CONTAINS_MACROS)
        }

        if (!WEB_PROTOCOL_RGX.test(protocol)) return Result.Err(IneligibleURLReason.PROTOCOL_NOT_ELIGIBLE)
        if (!DOMAIN_WITH_TLD_RGX.test(hostname)) return Result.Err(IneligibleURLReason.DOMAIN_NOT_ELIGIBLE)

        return Result.Ok(value)
    } catch (e) {
        return Result.Err(IneligibleURLReason.NOT_VALID_URL)
    }
}

const TemplateLandingUrlField = (props: TemplateLandingUrlFieldProps) => {
    const {
        domain,
        hidePageAnalysisToggle = false,
        hidePushlyAiToggle = false,
        onPageAnalysisClick,
        onPushlyAiClick,
        ...ttfProps
    } = props

    const hasAppendedActions = !hidePageAnalysisToggle || !hidePushlyAiToggle
    const [activeAction, setActiveAction] = useState<ActionId | null>(null)

    const isPageAnalysisActive = activeAction === ActionId.PAGE_ANALYSIS
    const isPushlyAiActive = activeAction === ActionId.PUSHLY_AI

    const enabledServices = useMemo(() => {
        const services: string[] = []
        if (!hidePageAnalysisToggle) {
            services.push('Page Analysis')
        }
        if (!hidePushlyAiToggle) {
            services.push('Pushly AI')
        }
        return services
    }, [domain, hidePageAnalysisToggle, hidePushlyAiToggle])

    const eligibleURL = getEligibleURL(ttfProps.value, domain)

    const isPageAnalysisDisabled = ttfProps.disabled || !ttfProps.value || isPushlyAiActive || eligibleURL.isErr()
    const isPushlyAiDisabled = ttfProps.disabled || !ttfProps.value || isPageAnalysisActive || eligibleURL.isErr()

    const actionMenuItems = useMemo(() => {
        const actions: (MenuItemGroupType | MenuItemType | MenuDividerType)[] = []

        if (!hidePushlyAiToggle) {
            actions.push({
                key: 'pushly-ai',
                type: 'group',
                label: (
                    <span>
                        <span>
                            <PushlyAi />
                        </span>
                        &nbsp; Pushly AI
                    </span>
                ),
                children: [
                    {
                        key: 'pushly-ai',
                        label: <span>Autofill</span>,
                        onClick: async () => {
                            try {
                                setActiveAction(ActionId.PUSHLY_AI)
                                await onPushlyAiClick?.(eligibleURL.unwrap(), 'auto')
                            } finally {
                                setActiveAction(null)
                            }
                        },
                    },
                    {
                        key: 'pushly-ai-advanced',
                        label: <span>Review & Customize</span>,
                        onClick: async () => {
                            try {
                                setActiveAction(ActionId.PUSHLY_AI)
                                await onPushlyAiClick?.(eligibleURL.unwrap(), 'advanced')
                            } finally {
                                setActiveAction(null)
                            }
                        },
                    },
                ],
            })
        }

        if (!hidePageAnalysisToggle) {
            if (!hidePushlyAiToggle) {
                actions.push({ type: 'divider' })
            }

            actions.push({
                key: 'page-analysis',
                type: 'group',
                label: (
                    <span>
                        <span>
                            <BulbOutlined />
                        </span>
                        &nbsp; Page Analysis
                    </span>
                ),
                children: [
                    {
                        key: 'page-analysis-auto',
                        label: <span>Autofill</span>,
                        onClick: async () => {
                            try {
                                setActiveAction(ActionId.PAGE_ANALYSIS)
                                await onPageAnalysisClick?.(eligibleURL.unwrap(), 'auto')
                            } finally {
                                setActiveAction(null)
                            }
                        },
                    },
                    {
                        key: 'page-analysis-advanced',
                        label: <span>Review & Customize</span>,
                        onClick: async () => {
                            try {
                                setActiveAction(ActionId.PAGE_ANALYSIS)
                                await onPageAnalysisClick?.(eligibleURL.unwrap(), 'advanced')
                            } finally {
                                setActiveAction(null)
                            }
                        },
                    },
                ],
            })
        }

        return actions
    }, [eligibleURL.unwrapOrNull(), isPageAnalysisDisabled, isPushlyAiDisabled])

    const ButtonWrapper = useMemo(() => {
        if (!eligibleURL.isErr()) {
            return Fragment
        }

        const errorMessage = eligibleURL.getErr().replace(ENABLED_SERVICES_TAG, enabledServices.join(' or '))

        return ({ children }: { children: ReactNode }) => (
            <Popover content={errorMessage}>
                <span>{children}</span>
            </Popover>
        )
    }, [eligibleURL.isErr() ? eligibleURL.getErr() : null])

    const actionsLoading = isPushlyAiActive || isPageAnalysisActive

    return (
        <div
            className={classnames(
                'template-landing-url-field',
                hasAppendedActions && 'template-landing-url-field__with-actions',
                !hidePageAnalysisToggle && 'template-landing-url-field__with-page-analysis',
                !hidePushlyAiToggle && 'template-landing-url-field__with-pushly-ai',
                props.className,
            )}
        >
            <TemplatizedTextField
                label={'Landing URL'}
                placeholder={'https://my.landing-page.domain/my-article'}
                {...ttfProps}
                hideEmojis={true}
                autoComplete="off"
            />

            {!!actionMenuItems.length && (
                <div className={classnames('template-landing-url-field-action', 'ant-row', 'ant-legacy-form-item')}>
                    <ButtonWrapper>
                        <Dropdown.Button
                            className={'template-landing-url-field-action-toggle'}
                            disabled={isPushlyAiDisabled || isPageAnalysisDisabled}
                            loading={actionsLoading}
                            icon={actionsLoading ? <Loading3QuartersOutlined spin={true} /> : <PushlyAi />}
                            menu={{ items: actionMenuItems }}
                            arrow={true}
                            placement="bottom"
                        />
                    </ButtonWrapper>
                </div>
            )}
        </div>
    )
}

export default TemplateLandingUrlField
